import TYPES from '../action-types';
import filterLightboards from '../helpers/lightboards/filterLightboards';
import getLightboardId from '../helpers/lightboards/getLightboardId';

const defaultState = {
  isLoaded: false,
  lightboards: [],
  filtredLightboards: null,
  activeLightboardID: null,
  activeLightboard: null,
  searchQuery: '',
  error: null,
  isBusy: false,
};

function setBusy(lightboards, lightboardId, isBusy) {
  if (!lightboards) return lightboards;
  return lightboards.map((lightboard) => (
    lightboard._id === lightboardId
      ? { ...lightboard, isBusy }
      : lightboard
  ));
}

function withBusy(state, payload, isBusy) {
  const lightboardId = getLightboardId(payload);
  return {
    ...state,
    lightboards: setBusy(state.lightboards, lightboardId, isBusy),
    filtredLightboards: setBusy(state.filtredLightboards, lightboardId, isBusy),
  };
}

function withLightboards(state, lightboards) {
  return {
    ...state,
    lightboards,
    filtredLightboards: filterLightboards(lightboards, state.searchQuery),
  };
}

export default function (state = defaultState, action) {
  const { type, payload, error } = action;

  const unsetActive = (actionType) => {
    if (actionType === TYPES.COLLECTIONS.SET_ACTIVE
      // || actionType === TYPES.KEYWORDS.SET_ACTIVE
      || actionType === TYPES.SAVEDSEARCHES.SET_ACTIVE
      || actionType === 'archive/setActiveCollection'
      || actionType === 'collections/setActiveCollection'
      || actionType === 'inboxes/setActiveInbox') {
      return actionType;
    }
    return null;
  };

  switch (type) {
  /* Get lightboards */
  case TYPES.LIGHTBOARDS.FETCH.START: {
    return {
      ...state,
      isLoaded: false,
      isLoading: true,
      error: null,
    };
  }
  case TYPES.LIGHTBOARDS.FETCH.COMPLETE: {
    return withLightboards({
      ...state,
      isLoaded: true,
      isLoading: false,
      error: null,
    }, payload.lightboards);
  }
  case TYPES.LIGHTBOARDS.FETCH.FAILED: {
    return {
      ...state,
      isLoaded: false,
      isLoading: false,
      error,
      lightboards: [],
      filtredLightboards: null,
    };
  }

  /* Set sort type */
  case TYPES.LIGHTBOARDS.SET_SORT_TYPE: {
    const lightboardId = getLightboardId(payload);
    return {
      ...state,
      sortType: payload.value,
      lightboards: state.lightboards.map((lightboard) => {
        const copy = { ...lightboard };
        if (lightboardId === copy._id) {
          copy.sortType = payload.value;
        }
        return copy;
      }),
      activeLightboard:
          state.activeLightboard._id === lightboardId
            ? {
              ...state.activeLightboard,
              sortType: payload.value,
            }
            : {
              ...state.activeLightboard,
            },
    };
  }

  /* Rename */
  case TYPES.LIGHTBOARDS.RENAME.START: {
    return withBusy(state, payload, true);
  }
  case TYPES.LIGHTBOARDS.RENAME.COMPLETE: {
    const lightboardId = getLightboardId(payload);
    const lightboards = state.lightboards.map((lightboard) => {
      if (lightboard._id !== lightboardId) return lightboard;
      return {
        ...lightboard,
        isBusy: false,
        path: payload.path,
        name: payload.name,
      };
    });
    return withLightboards(state, lightboards);
  }
  case TYPES.LIGHTBOARDS.RENAME.FAILED: {
    return {
      ...withBusy(state, payload, false),
      error: payload.error,
    };
  }

  /* Remove */
  case TYPES.LIGHTBOARDS.REMOVE.START: {
    return withBusy(state, payload, true);
  }
  case TYPES.LIGHTBOARDS.REMOVE.COMPLETE: {
    const lightboardId = getLightboardId(payload);
    return withLightboards(
      {
        ...state,
        activeLightboard: state.activeLightboard?._id === lightboardId
          ? null
          : state.activeLightboard,
      },
      state.lightboards.filter((lb) => lb._id !== lightboardId),
    );
  }
  case TYPES.LIGHTBOARDS.REMOVE.FAILED: {
    return {
      ...withBusy(state, payload, false),
      error: payload.error || error,
    };
  }

  /* Add */
  case TYPES.LIGHTBOARDS.ADD.START: {
    return {
      ...state,
      isBusy: true,
    };
  }
  case TYPES.LIGHTBOARDS.ADD.COMPLETE: {
    const { lightboard } = payload;
    return withLightboards(
      { ...state, isBusy: false },
      [...state.lightboards, lightboard],
    );
  }
  case TYPES.LIGHTBOARDS.ADD.FAILED: {
    return {
      ...state,
      isBusy: false,
      error,
    };
  }

  /* Search */
  case TYPES.LIGHTBOARDS.SEARCH: {
    return {
      ...state,
      searchQuery: payload.value,
      filtredLightboards: filterLightboards(state.lightboards, payload.value),
    };
  }

  /* Update sortType */
  case TYPES.LIGHTBOARDS.UPDATE_SORT_TYPE.START: {
    return {
      ...withBusy(state, payload, true),
      sortTypeUpdating: true,
    };
  }
  case TYPES.LIGHTBOARDS.UPDATE_SORT_TYPE.COMPLETE: {
    const lightboardId = getLightboardId(payload);
    const lightboards = state.lightboards.map((lightboard) => {
      if (lightboard._id !== lightboardId) return lightboard;
      return {
        ...lightboard,
        isBusy: false,
        sortType: payload.sortType,
      };
    });
    return {
      ...withLightboards(state, lightboards),
      sortTypeUpdating: false,
      sortType: payload.sortType,
      activeLightboard:
          state.activeLightboard._id === lightboardId
            ? {
              ...state.activeLightboard,
              sortType: payload.sortType,
            }
            : {
              ...state.activeLightboard,
            },
    };
  }
  case TYPES.LIGHTBOARDS.UPDATE_SORT_TYPE.FAILED: {
    return {
      ...withBusy(state, payload, false),
      sortTypeUpdating: false,
      error: payload.error,
    };
  }

  case 'lightboards/setActiveLightboard': {
    const { lightboard } = payload;

    return {
      ...state,
      activeLightboard: lightboard,
    };
  }

  case unsetActive(type): {
    if (state.activeLightboard) {
      return {
        ...state,
        activeLightboard: null,
      };
    }
  }

  /* Default */
  // eslint-disable-next-line no-fallthrough
  default: {
    return state;
  }
  }
}
